import './code-mirror-error-styles.css';

import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { closeLintPanel, diagnosticCount, linter, openLintPanel } from '@codemirror/lint';
import type { ReactCodeMirrorProps, ReactCodeMirrorRef } from '@uiw/react-codemirror';
import CodeMirror from '@uiw/react-codemirror';
import { Button, Loader } from '@wix/design-system';
import type { RicosEditorAPI } from '@wix/ricos';
import type { EditorPluginName } from '@wix/ricos/schema-next';
import { parseFromString } from '@wix/ricos/schema-next';
import { DuplicateSmall, QuickFormatSmall, StatusAlertSmall } from '@wix/wix-ui-icons-common';
import React from 'react';

import { toRcml } from '../app/ai-labs/rcml-converters';
import { fixDocument } from './autofix-api';
import { Card } from './card';
import { wixDocsCodeTheme } from './code-mirror-theme';
import DocumentHistory from './document-history';
import { safeParse } from './safe-parse';
import useCopy from './use-copy-code';
import type { HistoryMetadata } from './use-document-history';

export const DocumentCard: React.FC<{
  isAiLabsEnabled: boolean;
  contentHistory?: { content: object; version: string }[];
  headerRightButtons?: React.ReactNode;
  headerCenter?: React.ReactNode;
  footer?: React.ReactNode;
  stringContent: string;
  editorPlugins: EditorPluginName[];
  editorRef: RicosEditorAPI | null;
  onChange: (args: {
    content: string;
    immediate: boolean;
    historyUpdateProps?: { skip?: boolean; immediate?: boolean; historyMetadata?: HistoryMetadata };
  }) => void;
}> = ({
  isAiLabsEnabled,
  contentHistory,
  headerRightButtons,
  headerCenter,
  footer,
  stringContent,
  onChange,
  editorPlugins,
  editorRef,
}) => {
  const [hasErrors, setHasErrors] = React.useState(false);
  const [mode, setMode] = React.useState<'rcml' | 'json'>('json');
  const codemirrorRef = React.useRef<ReactCodeMirrorRef>(null);
  const isValidJson = getIsValidJson(stringContent);
  const [fixPending, setFixPending] = React.useState(false);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    editorRef
      ?.getEditorCommands()
      .setSpotlight(
        selectedNodeId ? [{ type: 'range', from: selectedNodeId, to: selectedNodeId }] : []
      );
  }, [selectedNodeId]);
  React.useEffect(() => {
    if (!hasErrors && codemirrorRef.current?.view) {
      closeLintPanel(codemirrorRef.current?.view);
    }
  }, [hasErrors]);
  const { copy: copyCode, message } = useCopy('Copy', 'Copied', stringContent);
  const tidyUp = () => {
    const result = safeParse(stringContent);
    if (result.type === 'ok') {
      onChange({
        content: JSON.stringify(result.value, null, 2),
        immediate: true,
        historyUpdateProps: { skip: true },
      });
    } else if (codemirrorRef.current?.view) {
      openLintPanel(codemirrorRef.current?.view);
    }
  };

  const headerLeft =
    contentHistory && contentHistory.length > 0 ? (
      <>
        {'Document'}{' '}
        <DocumentHistory
          contentHistory={contentHistory}
          setContent={content =>
            onChange({
              content: JSON.stringify(content, null, 2),
              immediate: true,
              historyUpdateProps: { skip: true },
            })
          }
        />
      </>
    ) : (
      'Document'
    );
  const headerRight = (
    <>
      {codemirrorRef.current && hasErrors && (
        <>
          {isValidJson && (
            <Button
              size="small"
              skin="standard"
              priority="secondary"
              onClick={() => {
                if (fixPending) {
                  return;
                }
                const result = safeParse(stringContent);
                if (result.type === 'ok') {
                  setFixPending(true);
                  fixDocument(result.value, editorPlugins)
                    .then(content => {
                      onChange({
                        content: JSON.stringify(content, null, 2),
                        immediate: true,
                        historyUpdateProps: { historyMetadata: { type: 'auto-fix' } },
                      });
                    })
                    .catch(e => {
                      console.error(e);
                    })
                    .finally(() => {
                      setFixPending(false);
                    });
                }
              }}
            >
              {fixPending && (
                <>
                  <Loader size="tiny" />
                  <span className="mr-2" />
                </>
              )}{' '}
              Auto-Fix
            </Button>
          )}
          <Button
            size="small"
            prefixIcon={<StatusAlertSmall />}
            skin="destructive"
            priority="primary"
            onClick={() => {
              codemirrorRef.current?.view && openLintPanel(codemirrorRef.current?.view);
            }}
          >
            Errors
          </Button>
        </>
      )}
      {headerRightButtons}
    </>
  );

  return (
    <Card
      backgroundColor="#000624"
      width="100%"
      header={{
        start: headerLeft,
        center: headerCenter,
        end: headerRight,
      }}
      footer={footer}
    >
      <>
        <div className="flex absolute top-[47px] right-0 z-10 gap-[1px]">
          {isValidJson && isAiLabsEnabled && (
            <CodeButton
              onClick={() => {
                setMode(s => (s === 'json' ? 'rcml' : 'json'));
              }}
              text={mode === 'json' ? 'RCML' : 'JSON'}
              icon={<QuickFormatSmall />}
            />
          )}
          {isValidJson && (
            <CodeButton
              onClick={tidyUp}
              text="Tidy up"
              icon={<QuickFormatSmall />}
              roundLeftBottom
            />
          )}
          <CodeButton onClick={copyCode} text={message} icon={<DuplicateSmall />} />
        </div>
        {mode === 'json' ? (
          <CodeMirror
            ref={codemirrorRef}
            theme={wixDocsCodeTheme}
            style={{ padding: '20px 8px 0' }}
            value={stringContent}
            minHeight="200px"
            extensions={
              [
                json(),
                codemirrorValidationPlugin(editorPlugins),
              ] as ReactCodeMirrorProps['extensions']
            }
            onChange={content => {
              onChange({
                content,
                immediate: false,
                historyUpdateProps: { historyMetadata: { type: 'code-editor' } },
              });
            }}
            onUpdate={updates => {
              const hasErrorsLatest = diagnosticCount(updates.state) > 0;
              if (hasErrorsLatest !== hasErrors) {
                setHasErrors(hasErrorsLatest);
              }
            }}
          />
        ) : (
          <CodeMirror
            readOnly
            theme={wixDocsCodeTheme}
            style={{ padding: '20px 8px 0' }}
            value={toRcml(JSON.parse(stringContent))}
            extensions={[xml()]}
            minHeight="200px"
            onUpdate={({ state, view }) => {
              if (view.hasFocus) {
                const line = state.doc.lineAt(state.selection.main.from);
                const nodeId = genNodeIdFromSelection(line.text);
                if (nodeId !== selectedNodeId) {
                  setSelectedNodeId(nodeId ? nodeId : undefined);
                }
              }
            }}
          />
        )}
      </>
    </Card>
  );
};

const genNodeIdFromSelection = (line: string) => {
  const match = line.match(/id="([^"]+)"/);
  return match ? match[1] : undefined;
};

export const codemirrorValidationPlugin = (editorPlugins: EditorPluginName[]) => {
  // TODO: Implement validation for schema features by using editor plugin names
  return linter(view => {
    const result = parseFromString(view.state.doc.toString(), { plugins: editorPlugins });
    if (result.type === 'ok') {
      return [];
    }
    return result.messages.map(message => ({
      ...message,
      severity: 'error',
    }));
  });
};

const CodeButton: React.FC<{
  className?: string;
  onClick: () => void;
  text: string | React.ReactNode;
  icon: React.ReactNode;
  roundLeftBottom?: boolean;
}> = ({ onClick, text, icon, className, roundLeftBottom }) => {
  return (
    <button
      className={`${className} flex cursor-pointer gap-1.5 ${
        roundLeftBottom ? 'rounded-bl-md' : ''
      } bg-slate-900 py-1.5 pl-1.5 pr-2 text-sm leading-[18px] text-white hover:bg-slate-850 hover:text-slate-100`}
      onClick={onClick}
    >
      {icon} {text}
    </button>
  );
};

const getIsValidJson = (json: string) => {
  try {
    JSON.parse(json);
    return true;
  } catch (e) {
    return false;
  }
};
